import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  createAdvisoryTemplate,
  updateAdvisoryTemplate,
} from "api/advisoryTemplate";
import {
  CONTENT_MAX,
  NAME_MAX,
  TEMPLATE_STATUS,
  buildAdvisoryPayload,
  templateStatusOf,
  validateAdvisoryForm,
} from "./advisoryTemplateForm";

const specialityOf = (data, options) => {
  const id = typeof data?.specialization === "string" ? data.specialization : data?.specialization?._id;
  if (!id) return null;
  return (
    options.find((option) => option._id === id) || {
      _id: id,
      name: data?.specialityLabel || data?.specialization?.name || "",
    }
  );
};

/**
 * Add / Edit one DRCFO Advisory Template: name, ONE speciality (an Advisory
 * template is shown to the doctors of the speciality it names), status and
 * the advice text — the same `generalAdvice` a doctor's Advisory template
 * holds, one point per line.
 */
const AdvisoryTemplateDialog = ({ open, onClose, data, specialityOptions = [] }) => {
  const recordId = data?._id;
  const isUpdate = Boolean(recordId);

  const [name, setName] = useState("");
  const [speciality, setSpeciality] = useState(null);
  const [templateStatus, setTemplateStatus] = useState(TEMPLATE_STATUS.PUBLISHED);
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(data?.name || "");
    setSpeciality(specialityOf(data, specialityOptions));
    setTemplateStatus(isUpdate ? templateStatusOf(data) : TEMPLATE_STATUS.PUBLISHED);
    setContent(data?.generalAdvice || "");
  }, [open, data, isUpdate, specialityOptions]);

  const validationError = useMemo(
    () => validateAdvisoryForm({ name, speciality, content }),
    [name, speciality, content]
  );

  const handleSubmit = async () => {
    if (validationError) return;
    setSaving(true);
    const payload = buildAdvisoryPayload({ name, speciality, content, templateStatus });
    try {
      if (isUpdate) {
        await updateAdvisoryTemplate(recordId, payload);
        toast.success("Advisory template updated successfully");
      } else {
        await createAdvisoryTemplate(payload);
        toast.success("Advisory template created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} the advisory template`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose("close")} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isUpdate ? "Edit Advisory Template" : "Add Advisory Template"}
        <Box component="span" sx={{ display: "block", fontSize: "0.8rem", color: "#7D8DC4" }}>
          Published advisories are offered, read-only, to every doctor of the selected speciality.
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <TextField
            label="Advisory name"
            required
            fullWidth
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ "data-testid": "advisory-name", maxLength: NAME_MAX + 20 }}
          />

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Autocomplete
              size="small"
              sx={{ flex: 2, minWidth: 240 }}
              options={specialityOptions}
              value={speciality}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onChange={(event, next) => setSpeciality(next)}
              renderInput={(params) => (
                <TextField {...params} label="Speciality" required placeholder="Select a speciality" />
              )}
            />

            <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
              <InputLabel id="advisory-status-label">Status</InputLabel>
              <Select
                labelId="advisory-status-label"
                label="Status"
                value={templateStatus}
                onChange={(event) => setTemplateStatus(event.target.value)}
                SelectDisplayProps={{ "data-testid": "advisory-status" }}
              >
                <MenuItem value={TEMPLATE_STATUS.PUBLISHED}>Published</MenuItem>
                <MenuItem value={TEMPLATE_STATUS.DRAFT}>Draft</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TextField
            label="Advisory content"
            required
            fullWidth
            multiline
            minRows={8}
            maxRows={18}
            value={content}
            onChange={(event) => setContent(event.target.value)}
            helperText={`One advice point per line. ${content.trim().length} / ${CONTENT_MAX} characters.`}
            inputProps={{ "data-testid": "advisory-content", maxLength: CONTENT_MAX + 100 }}
          />

          {validationError && (
            <Box role="alert" sx={{ color: "#d32f2f", fontSize: "0.8rem" }}>
              {validationError}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "1rem 1.5rem" }}>
        <Button onClick={() => onClose("close")} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || Boolean(validationError)}
          sx={{ bgcolor: "#194AF5", ":hover": { bgcolor: "#1540d6" } }}
        >
          {saving ? <CircularProgress size="1.2rem" sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvisoryTemplateDialog;
